import React, { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { AlertIcon, ArrowLeftIcon, Spinner } from './AuthModal';
import { sendOtp, verifyOtp, type OtpPurpose } from '../../api/otpService';
import '../../assets/css/otp.css';

interface OtpVerifierProps {
  mobile: string;
  purpose: OtpPurpose;
  /** seconds until "Resend" unlocks (first OTP is sent by the parent) */
  initialResendIn?: number;
  otpLength?: number;
  onVerified: (otpToken: string) => void | Promise<void>;
  onBack: () => void;
  backLabel?: string;
  /** shown while parent finishes work after verification (e.g. creating account) */
  busyLabel?: string;
}

const OtpVerifier = ({
  mobile,
  purpose,
  initialResendIn = 60,
  otpLength = 6,
  onVerified,
  onBack,
  backLabel = 'Change number',
  busyLabel = 'Verifying…',
}: OtpVerifierProps) => {
  const [digits, setDigits] = useState<string[]>(Array(otpLength).fill(''));
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(initialResendIn);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const submittedRef = useRef('');

  // countdown
  useEffect(() => {
    if (resendIn <= 0) return;
    const t = setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  useEffect(() => { inputs.current[0]?.focus(); }, []);

  const otp = digits.join('');

  const submit = async (code: string) => {
    if (code.length !== otpLength || verifying) return;
    submittedRef.current = code;
    setError(''); setInfo(''); setVerifying(true);
    const res = await verifyOtp(mobile, purpose, code);
    if (res.ok && res.data.otp_token) {
      try {
        await onVerified(res.data.otp_token);
      } finally {
        setVerifying(false);
      }
      return;
    }
    setVerifying(false);
    setError(res.message);
    setDigits(Array(otpLength).fill(''));
    inputs.current[0]?.focus();
  };

  // auto-submit when all digits filled
  useEffect(() => {
    if (otp.length === otpLength && otp !== submittedRef.current) submit(otp);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp]);

  const setAt = (idx: number, val: string) => {
    setDigits((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };

  const handleChange = (idx: number, raw: string) => {
    const clean = raw.replace(/\D/g, '');
    setError('');
    if (!clean) { setAt(idx, ''); return; }
    if (clean.length > 1) {
      // paste / autofill of full code
      const next = Array(otpLength).fill('');
      clean.slice(0, otpLength).split('').forEach((d, i) => { next[i] = d; });
      setDigits(next);
      inputs.current[Math.min(clean.length, otpLength) - 1]?.focus();
      return;
    }
    setAt(idx, clean);
    if (idx < otpLength - 1) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
      setAt(idx - 1, '');
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      inputs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < otpLength - 1) {
      inputs.current[idx + 1]?.focus();
    } else if (e.key === 'Enter') {
      submit(otp);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || resending) return;
    setResending(true); setError(''); setInfo('');
    const res = await sendOtp(mobile, purpose);
    setResending(false);
    if (res.ok) {
      setInfo('A new OTP has been sent.');
      setResendIn(res.data.resend_in ?? 60);
      submittedRef.current = '';
      setDigits(Array(otpLength).fill(''));
      inputs.current[0]?.focus();
    } else {
      setError(res.message);
      if (res.data.retry_after) setResendIn(res.data.retry_after);
    }
  };

  return (
    <div className="ffa-otp">
      <button type="button" className="ffa-back" onClick={onBack} disabled={verifying}>
        <ArrowLeftIcon /> {backLabel}
      </button>

      <p className="ffa-eyebrow">Verify mobile</p>
      <h2 className="ffa-title">Enter the <em>OTP</em></h2>
      <p className="ffa-subtitle" style={{ marginBottom: 12 }}>
        We sent a {otpLength}-digit code by SMS to
      </p>
      <div className="ffa-email-pill">+91 {mobile}</div>

      {error && (
        <div className="ffa-alert" role="alert">
          <AlertIcon />
          <span>{error}</span>
        </div>
      )}
      {info && !error && <div className="ffa-otp-info" role="status">{info}</div>}

      <div className="ffa-otp-boxes" role="group" aria-label="One-time password">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { inputs.current[i] = el; }}
            className={`ffa-otp-box${error ? ' ffa-otp-box--error' : ''}`}
            type="text"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={i === 0 ? otpLength : 1}
            value={d}
            disabled={verifying}
            aria-label={`Digit ${i + 1}`}
            onChange={(e) => handleChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onFocus={(e) => e.target.select()}
          />
        ))}
      </div>

      <button
        type="button"
        className="ffa-btn"
        onClick={() => submit(otp)}
        disabled={verifying || otp.length !== otpLength}
      >
        {verifying ? <><Spinner /> {busyLabel}</> : 'Verify OTP'}
      </button>

      <p className="ffa-footer ffa-otp-resend">
        {resendIn > 0 ? (
          <>Resend OTP in <strong>{resendIn}s</strong></>
        ) : (
          <>
            Didn't get the code?{' '}
            <button type="button" className="ffa-link" onClick={handleResend} disabled={resending}>
              {resending ? 'Sending…' : 'Resend OTP'}
            </button>
          </>
        )}
      </p>
    </div>
  );
};

export default OtpVerifier;